// Apolo IDE — GitConnectionBar (Sprint github E1).
//
// A slim liquid-glass control bar that sits ABOVE the Code chat composer and
// surfaces the git/GitHub connection the way Pedro wanted to SEE + USE it:
//   1. repo (origin short name) + current branch + the +N/−M diff stat
//      (the "+291 −11" from the screenshot) — read live via Electron git IPC
//      through useGitConnection.
//   2. the repo's OPEN pull requests, compact + collapsible, with a merge/CI
//      badge — reusing PullRequestCard (same mapCiStatus semantics).
//   3. functional actions: primary "Criar PR" (reuses AutoPRButton/useAutoPR),
//      plus secondary clone (copy clone URL) + fork (POST /api/github/.../fork).
//
// It ASSEMBLES existing pieces; it does not reimplement PR/git logic. States
// (loading / no-repo / not-connected / error) follow 01-UI-DOCTRINE.md.

import { useCallback, useState } from 'react';
import {
  GitPullRequest,
  GitFork,
  Copy,
  Check,
  ChevronDown,
  Loader2,
  Github,
  RefreshCw,
  AlertTriangle,
  FolderOpen,
  Plus,
} from 'lucide-react';
import ComposerOverflowMenu from './ComposerOverflowMenu.jsx';
import { useGitConnection } from '../../hooks/useGitConnection.js';
import { useAgentStore } from '../../store/useAgentStore.js';
import { apiFetch } from '../../lib/api.js';
import { openFolderForSession } from '../../lib/openFolder.js';
import { resolveGitContext } from '../../lib/gitContext.js';
import GitContextHeader from './GitContextHeader.jsx';
import AutoPRButton from './AutoPRButton.jsx';
import PullRequestCard from './PullRequestCard.jsx';
import RepoSlugMenu from './RepoSlugMenu.jsx';
import BranchMenu from './BranchMenu.jsx';
import CIMonitorMenu from './CIMonitorMenu.jsx';
import { useGitChecks } from '../../hooks/useGitChecks.js';
import { useBranchPr } from '../../hooks/useBranchPr.js';
// (Pedro 2026-06-25, sprint cursor-strip-4 round 2) — ComposerOverflowMenu
// reusado pro overflow "+" das ações de repo. Import acima junto do Plus.
import { Activity } from 'lucide-react';

const GLASS =
  'rounded-xl border border-apolo-borderSubtle bg-apolo-surface1/80 backdrop-blur-md ' +
  'shadow-[var(--apolo-glass-card-shadow)]';

// Open the connect-GitHub flow (same event RepoPickerPill dispatches).
function openConnections() {
  window.dispatchEvent(new CustomEvent('apolo:openConnections'));
}

export default function GitConnectionBar({ sessionId, session, children }) {
  const root = session?.directory || '';
  const git = useGitConnection({ root });
  // (Pedro 2026-06-25, sprint cursor-strip-3) — drop-ups: repo picker no slug
  // e branch switcher no chip de branch. Ambos via IPC real (sem mockup).
  const [repoMenuOpen, setRepoMenuOpen] = useState(false);
  const [branchMenuOpen, setBranchMenuOpen] = useState(false);
  const [ciMenuOpen, setCiMenuOpen] = useState(false);
  // CI rollup leve no chip da bar (poll 60s). O drop-up reusa o mesmo hook
  // com pollMs=30s quando aberto pra ter dados mais frescos.

  // Selected repo from the composer prefs — used for clone/fork fallback when
  // there's no live worktree yet (home composer before "Criar sessão").
  const selectedRepo = useAgentStore((s) => s.code.ui.github_repo) || null;

  // Âncora autoritativa (sprint composer-anchor E7): o alvo da sessão é o git
  // VIVO do worktree, resolvido pelo core puro reusando os dados que o
  // useGitConnection já leu (sem IPC dobrado). A pref entra só como FALLBACK
  // quando não há git vivo (sessão pré-clone) — nunca por cima de um origin
  // real, pra não gerar falso conflito por uma pref stale da home.
  const liveOrigin = git.origin
    ? { ok: true, owner: git.origin.owner, repo: git.origin.repo, url: git.origin.url }
    : null;
  const anchorContext = resolveGitContext({
    liveOrigin,
    liveBranch: git.branch,
    dir: root || null,
    selectedRepo: liveOrigin ? null : selectedRepo,
  });

  const [prsOpen, setPrsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [forkPhase, setForkPhase] = useState('idle'); // idle|forking|done|error
  const [forkResult, setForkResult] = useState(null); // { full_name, html_url }
  const [actionError, setActionError] = useState(null);
  const [openingFolder, setOpeningFolder] = useState(false);

  // Cowork composer (E1) — rebind the session to ANY picked folder (git optional).
  const updateCodeSession = useAgentStore((s) => s.updateCodeSession);
  const canPickFolder =
    typeof window !== 'undefined' && typeof window.apolo?.repo?.pickFolder === 'function';
  const handleOpenFolder = useCallback(async () => {
    if (!sessionId) return;
    setActionError(null);
    setOpeningFolder(true);
    const res = await openFolderForSession(sessionId, { rebind: updateCodeSession });
    setOpeningFolder(false);
    if (!res.ok && res.error && res.error !== 'Seleção cancelada.') setActionError(res.error);
  }, [sessionId, updateCodeSession]);

  // origin owner/repo (live) wins; else fall back to the selected repo pref.
  const owner = git.origin?.owner || selectedRepo?.full_name?.split('/')[0] || null;
  const repo = git.origin?.repo || selectedRepo?.name || null;
  // (Pedro 2026-06-25, sprint cursor-strip-3) — light CI rollup pra colorir
  // o chip "CI" no canto direito. Polling de 60s na bar; 30s quando o
  // drop-up está aberto (CIMonitorMenu reusa o hook).
  const branchForCi = git.branch || null;
  const ciLight = useGitChecks({
    owner,
    repo,
    ref: branchForCi,
    enabled: !!(owner && repo && branchForCi),
    pollMs: 60_000,
  });
  // (Pedro 2026-06-25, sprint cursor-strip-4 round 7) — PR aberta do BRANCH
  // ATUAL via endpoint dedicado (determinístico). Substitui o # da bolinha de
  // status no header. A heurística antiga (head_ref em openPrs) não pegava
  // porque o enrichment só cobre os 10 primeiros da busca involves:.
  const { pr: branchPrLive } = useBranchPr({
    owner,
    repo,
    branch: branchForCi,
    enabled: !!(owner && repo && branchForCi),
  });
  // E1 — git actions (clone/fork/PR) only make sense on a real git repo. A folder
  // opened in 'direct' mode without a .git has no origin → hide them.
  const hasGit = !!git.origin;
  const cloneUrl = git.origin?.url || selectedRepo?.clone_url || null;

  const handleClone = useCallback(async () => {
    if (!cloneUrl) return;
    setActionError(null);
    try {
      await navigator.clipboard.writeText(cloneUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setActionError('Não foi possível copiar a URL de clone.');
    }
  }, [cloneUrl]);

  const handleFork = useCallback(async () => {
    if (!owner || !repo) return;
    setActionError(null);
    setForkPhase('forking');
    const res = await apiFetch(`/api/github/repos/${owner}/${repo}/fork`, { method: 'POST' });
    if (!res.ok) {
      if (res.status === 404 || res.code === 'GITHUB_NOT_CONNECTED') {
        setActionError('Conecte o GitHub para forkar.');
      } else {
        setActionError(res.error || 'Falha ao forkar.');
      }
      setForkPhase('error');
      return;
    }
    setForkResult(res.data?.fork || null);
    setForkPhase('done');
  }, [owner, repo]);

  // ── Hidden: feature only makes sense in the Electron app ──────────────────
  if (git.connection === 'no-electron') return null;

  // ── Loading skeleton (no long spinner — a calm shimmer-less placeholder) ──
  if (git.connection === 'loading') {
    return (
      <div className={`${GLASS} flex items-center gap-2 px-3 py-2`} aria-busy="true" aria-label="Carregando conexão git">
        <Loader2 size={14} className="animate-spin text-apolo-textTertiary motion-reduce:animate-none" aria-hidden="true" />
        <span className="text-[11px] text-apolo-textTertiary">Lendo git…</span>
      </div>
    );
  }

  // ── Error + retry ─────────────────────────────────────────────────────────
  if (git.connection === 'error') {
    return (
      <div className={`${GLASS} flex items-center justify-between gap-2 px-3 py-2`} role="alert">
        <span className="inline-flex items-center gap-1.5 text-[11px] text-apolo-error">
          <AlertTriangle size={13} aria-hidden="true" />
          {git.error || 'Não foi possível ler o git.'}
        </span>
        <button
          type="button"
          onClick={() => git.refresh()}
          aria-label="Tentar de novo"
          className="inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded-md border border-apolo-borderSubtle px-2 text-[11px] text-apolo-textSecondary transition-colors hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
        >
          <RefreshCw size={12} aria-hidden="true" />
          Tentar de novo
        </button>
      </div>
    );
  }

  const diff = git.diffStat;
  const prs = git.openPrs || [];
  // (Pedro 2026-06-25, sprint cursor-strip-4 round 7) — # da PR do branch atual.
  // FONTE PRIMÁRIA = endpoint dedicado (branchPrLive, determinístico). Fallback
  // = match por head_ref em openPrs (caso o endpoint ainda não tenha respondido).
  // Normaliza os dois formatos pra { pr_number, html_url }.
  const branchPrFallback = prs.find(
    (p) => p.head_ref && git.branch && p.head_ref === git.branch,
  );
  const branchPrNumber = branchPrLive?.number ?? branchPrFallback?.pr_number ?? null;
  const branchPrUrl = branchPrLive?.html_url ?? branchPrFallback?.html_url ?? null;
  // Estado da PR pra colorir o # (merged=roxo, open=verde, closed=vermelho).
  const branchPrState = branchPrLive?.merged
    ? 'merged'
    : branchPrLive?.state || (branchPrFallback ? 'open' : null);

  return (
    <div className={`${GLASS} flex w-full flex-col gap-2 px-3 py-2`} aria-label="Conexão git">
      {/* (Pedro 2026-06-25, sprint cursor-strip-2 round 2) — UMA single row
         que NUNCA quebra os botões pra outra linha:
           ┌────────────────────────── outer flex-nowrap ──────────────────────────┐
           │  [identidade · +47 -15 · Auto-reparo · Roteamento] (flex-1, wraps    │
           │   internamente se super-apertado)                                    │
           │                          ──────── ml-auto ────────                    │
           │  [Abrir pasta · Clone · Fork · Auto PR · N PR aberto ⌄] (shrink-0)   │
           └──────────────────────────────────────────────────────────────────────┘
         O cluster de botões (incluindo o trigger de PR) AGORA vive numa única
         div shrink-0 — Pedro reclamou que "Abrir pasta/Clone/Fork/Auto PR + N
         PR aberto" estavam em divs separadas e quebravam pra rows diferentes.
         A LISTA EXPANDIDA de PRs ainda vive em col-wrap abaixo (são cards). */}
      <div className="flex flex-nowrap items-center gap-x-3">
        {/* LEFT cluster — identidade + diff + children. min-w-0 + flex-wrap pra
           encolher graciosamente; nunca empurra os botões pra baixo. */}
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
          {/* Identidade autoritativa do alvo — repo · branch · dir · saúde.
              inline=true tira o pb-2.5/border-b e o ml-auto da saúde.
              (Pedro 2026-06-25 sprint cursor-strip-3) — slug abre repo picker
              real, branch abre o switcher real. */}
          <div className="relative inline-flex items-center">
            <GitContextHeader
              context={anchorContext}
              mode={session?.mode === 'direct' ? 'direct' : 'worktree'}
              inline
              onSlugClick={() => setRepoMenuOpen((v) => !v)}
              onBranchClick={() => setBranchMenuOpen((v) => !v)}
              prNumber={branchPrNumber}
              prUrl={branchPrUrl}
              prState={branchPrState}
            />
            <RepoSlugMenu
              open={repoMenuOpen}
              onClose={() => setRepoMenuOpen(false)}
              sessionId={sessionId}
              currentPath={root}
              rebind={updateCodeSession}
            />
            <BranchMenu
              open={branchMenuOpen}
              onClose={() => setBranchMenuOpen(false)}
              root={root}
              currentBranch={anchorContext?.branch}
              onAfterSwitch={() => git.refresh?.()}
            />
          </div>

          {/* Diff stat ao LADO da identidade — Pedro pediu +47 -15 perto de
             pedromleal15/apolo-IDE · feat/learn-loop-opens-pr · ~/Apolo.
             (Pedro 2026-06-25 round 3) — sempre visível: quando tree limpo
             mostra +0 −0 em vez de sumir. Pedro reclamou que "sumiu" e quer
             a info SEMPRE no slot. Real (vem de git.diffStat via
             useGitConnection); 0/0 é dado verdadeiro, não mockup. */}
          {git.connection !== 'no-electron' && (
            <span
              className="font-mono text-[11px] tabular-nums"
              aria-label={`${diff?.additions ?? 0} adições, ${diff?.deletions ?? 0} remoções`}
              data-testid="git-diff-stat"
              title={diff ? 'Mudanças não-commitadas no worktree' : 'Worktree limpo'}
            >
              <span className="text-apolo-textSecondary">+{diff?.additions ?? 0}</span>{' '}
              <span className="text-apolo-textTertiary">−{diff?.deletions ?? 0}</span>
            </span>
          )}

          {/* (Pedro 2026-06-25, sprint cursor-strip-4 round 2) — Auto-reparo /
             Roteamento saíram daqui; agora vivem DENTRO do overflow "+" no
             cluster direito, junto de Abrir pasta/Clone/Fork/Auto PR. */}
        </div>

        {/* RIGHT cluster — overflow "+" (Auto-reparo/Roteamento/Abrir pasta/
           Clone/Fork/Auto PR) + CI + trigger de PR. shrink-0 garante que NUNCA
           se parte nem cai pra row de baixo. */}
        <div className="flex shrink-0 items-center gap-1.5">
          {/* (Pedro 2026-06-25, sprint cursor-strip-4 round 2) — os 6 botões
             secundários colapsaram num único overflow "+" (Pedro: "coloque
             esses botões dentro do botão mais opções e troque o ícone por +").
             CI + PRs ficam fora (visíveis). */}
          <ComposerOverflowMenu
            label="Ações do repositório"
            icon={Plus}
            align="right"
            triggerClassName={
              'inline-flex h-7 min-w-7 items-center justify-center gap-1 rounded-full border border-apolo-borderSubtle bg-apolo-surface2 px-2.5 text-[11px] text-apolo-textSecondary transition-colors hover:bg-apolo-surface3 hover:text-apolo-textPrimary'
            }
          >
            {/* Auto-reparo / Roteamento (vinham do pai como children/sessionControls) */}
            {children && (
              <div className="flex flex-col gap-1 border-b border-apolo-borderSubtle pb-1">
                {children}
              </div>
            )}
            {/* Abrir pasta */}
            {canPickFolder && (
              <button
                type="button"
                role="menuitem"
                onClick={handleOpenFolder}
                disabled={openingFolder}
                aria-label="Abrir pasta como workspace"
                title="Abrir pasta (git opcional)"
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-apolo-textSecondary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary disabled:cursor-not-allowed disabled:opacity-40"
              >
                {openingFolder ? (
                  <Loader2 size={13} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
                ) : (
                  <FolderOpen size={13} aria-hidden="true" />
                )}
                Abrir pasta
              </button>
            )}
            {/* git actions — only when the folder is actually a git repo (E1 gating) */}
            {hasGit && (
              <>
                {/* clone — copy URL */}
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleClone}
                  disabled={!cloneUrl}
                  aria-label={copied ? 'URL de clone copiada' : 'Copiar URL de clone'}
                  title={cloneUrl ? (copied ? 'Copiado!' : `Copiar: ${cloneUrl}`) : 'Sem URL de clone'}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-apolo-textSecondary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {copied ? <Check size={13} aria-hidden="true" className="text-apolo-success" /> : <Copy size={13} aria-hidden="true" />}
                  {copied ? 'Copiado' : 'Clone (copiar URL)'}
                </button>

                {/* fork */}
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleFork}
                  disabled={!owner || !repo || forkPhase === 'forking'}
                  aria-label="Forkar repositório"
                  title={owner && repo ? `Forkar ${owner}/${repo}` : 'Sem repositório para forkar'}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-apolo-textSecondary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {forkPhase === 'forking' ? (
                    <Loader2 size={13} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  ) : (
                    <GitFork size={13} aria-hidden="true" />
                  )}
                  Fork
                </button>

                {/* primary CTA — Criar PR (reuses the existing, functional flow) */}
                <div className="px-0.5 pt-0.5">
                  <AutoPRButton sessionId={sessionId} session={session} />
                </div>
              </>
            )}
          </ComposerOverflowMenu>

          {/* (Pedro 2026-06-25, sprint cursor-strip-3 T11) — chip de CI com
             rollup do GitHub Actions. Cor do dot reflete o estado:
               pass=verde, fail=vermelho, pending=âmbar, none=cinza.
             Click abre o CIMonitorMenu (Aprovado/Falhou/Em andamento + lista
             + 2 toggles). Hidden se sem owner/repo/branch. */}
          {hasGit && owner && repo && branchForCi && (
            <div className="relative inline-flex">
              <button
                type="button"
                onClick={() => setCiMenuOpen((v) => !v)}
                aria-expanded={ciMenuOpen}
                aria-label="Monitoramento de CI"
                title="Monitoramento de CI"
                data-testid="ci-monitor-trigger"
                className="inline-flex h-7 min-w-7 items-center justify-center gap-1.5 rounded-full border border-apolo-borderSubtle bg-apolo-surface2 px-2.5 text-[11px] text-apolo-textSecondary transition-colors hover:bg-apolo-surface3 hover:text-apolo-textPrimary active:scale-[0.97] motion-reduce:active:scale-100"
              >
                <Activity size={12} aria-hidden="true" />
                <span
                  aria-hidden="true"
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{
                    background:
                      ciLight.data?.rollup === 'pass'
                        ? 'var(--apolo-success, #16a34a)'
                        : ciLight.data?.rollup === 'fail'
                        ? 'var(--apolo-error, #dc2626)'
                        : ciLight.data?.rollup === 'pending'
                        ? 'var(--apolo-warn, #f59e0b)'
                        : 'var(--apolo-textTertiary, #6b7280)',
                  }}
                />
                <span className="hidden sm:inline">CI</span>
              </button>
              <CIMonitorMenu
                open={ciMenuOpen}
                onClose={() => setCiMenuOpen(false)}
                owner={owner}
                repo={repo}
                gitRef={branchForCi}
              />
            </div>
          )}

          {/* (Pedro 2026-06-25, sprint cursor-strip-2 round 2) — trigger do PR
             list AGORA vive DENTRO do cluster direito (era irmão fora, e quebrava
             pra row separada quando o viewport ficava apertado). */}
          {prs.length > 0 && (
            <button
              type="button"
              onClick={() => setPrsOpen((v) => !v)}
              aria-expanded={prsOpen}
              aria-controls="git-bar-prs"
              className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border border-apolo-borderSubtle bg-apolo-surface2 px-2.5 text-[11px] font-medium text-apolo-textSecondary transition-colors hover:bg-apolo-surface3 hover:text-apolo-textPrimary"
            >
              <GitPullRequest size={12} aria-hidden="true" />
              {prs.length} PR{prs.length > 1 ? 's' : ''} aberto{prs.length > 1 ? 's' : ''}
              <ChevronDown
                size={12}
                aria-hidden="true"
                className={`transition-transform duration-200 motion-reduce:transition-none ${prsOpen ? 'rotate-180' : ''}`}
              />
            </button>
          )}
        </div>
      </div>

      {/* action feedback (clone error / fork result) */}
      {actionError && (
        <p role="alert" className="text-[11px] text-apolo-error">{actionError}</p>
      )}
      {forkPhase === 'done' && forkResult && (
        <p role="status" className="inline-flex items-center gap-1 text-[11px] text-apolo-success">
          <Check size={12} aria-hidden="true" />
          Fork criado:{' '}
          <a href={forkResult.html_url} target="_blank" rel="noreferrer" className="underline">
            {forkResult.full_name}
          </a>
        </p>
      )}

      {/* not-connected hint (git ok but GitHub PRs are mock = no token) */}
      {git.connection === 'not-connected' && (
        <button
          type="button"
          onClick={openConnections}
          className="inline-flex w-fit items-center gap-1.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 text-[11px] font-medium text-apolo-textSecondary transition-colors hover:border-apolo-accent/40 hover:text-apolo-textPrimary"
        >
          <Github size={12} aria-hidden="true" />
          Conectar GitHub para PRs reais
        </button>
      )}

      {/* (Pedro 2026-06-25, sprint cursor-strip-2) — só a LISTA expandida
         de PRs vive aqui embaixo; o TRIGGER ("N PRs abertos ⌄") foi
         promovido pra row consolidada acima. */}
      {prs.length > 0 && prsOpen && (
        <ul id="git-bar-prs" className="flex flex-col gap-2">
          {prs.slice(0, 5).map((pr) => (
            <li key={pr.id}>
              {pr.html_url ? (
                <a href={pr.html_url} target="_blank" rel="noreferrer" className="block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent">
                  <PullRequestCard pr={pr} />
                </a>
              ) : (
                <PullRequestCard pr={pr} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
